import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Tables } from "database.types";
import { historyForPeriod, ownedMarketHistory, variantMarketHistory, type OwnedHistoryItem } from "@/components/pricing/history";
import { formatPrice } from "@/utils/formatPrice";

interface BinderValueChartProps {
  binderId?: string;
  currentValue?: number | null;
  variants: Tables<"variants">[];
  ownedItems?: OwnedHistoryItem[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const isVisible = active && payload && payload.length;

  const date = label;
  const value = payload?.[0]?.value;

  const formattedValue = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }, [value]);
  return (
    <div
      className="bg-background border border-border rounded-md px-2 py-1 shadow-sm flex items-center gap-2"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
    >
      <span>{formattedValue}</span>
      <span className="text-xs text-muted-foreground">{date}</span>
    </div>
  );
};

const BinderValueChart: React.FC<BinderValueChartProps> = ({ variants, currentValue, ownedItems }) => {
  const [currentPeriod, setCurrentPeriod] = useState("30d");

  const periods = useMemo(() => {
    return [
      { name: "3d", days: 3 },
      { name: "7d", days: 7 },
      { name: "15d", days: 15 },
      { name: "30d", label: "1M", days: 30 },
      { name: "90d", label: "3M", days: 90 },
      { name: "180d", label: "6M", days: 180 },
      { name: "1y", label: "1Y", days: 365 },
    ];
  }, []);

  const currentTotalPrice = useMemo(() => {
    if (currentValue !== undefined) return currentValue;
    if (!variants.length) return 0;
    if (variants.length <= 1) {
      return Number(variants[0].price);
    }
    return variants.reduce((acc, v) => {
      return acc + (Number(v.price) || 0);
    }, 0);
  }, [variants, currentValue]);

  const coverage = useMemo(() => ownedItems ? ownedMarketHistory(ownedItems) : null, [ownedItems]);
  const recordedHistory = useMemo(() => {
    // A card chart uses one exact variant, never the sum of different conditions.
    return coverage?.points ?? (variants.length === 1 ? variantMarketHistory(variants[0].raw) : []);
  }, [coverage, variants]);
  const chartData = useMemo(() => {
    const period = periods.find((item) => item.name === currentPeriod)!;
    return historyForPeriod(recordedHistory, period.days).map((point) => ({
      date: new Date(point.t * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      value: point.p,
      fullDate: new Date(point.t * 1000).toISOString(),
    }));
  }, [recordedHistory, currentPeriod, periods]);

  // Calculate change from first to last value
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;

  const change = lastValue - firstValue;
  const changePercent =
    firstValue > 0 ? Math.abs((change / firstValue) * 100) : "";

  const changeType =
    change === 0 ? "neutral" : change > 0 ? "positive" : "negative";

  const currentPriceFormatted = currentTotalPrice === null ? "Unpriced" : formatPrice(currentTotalPrice);

  return (
    <Card className="bg-white dark:bg-card border border-gray-200 dark:border-border">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground dark:text-fluorescent-blue flex items-center justify-between">
          <span>Value History</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-normal text-gray-600 dark:text-muted-foreground">
              {currentPriceFormatted}
            </span>
            {change !== 0 && (
              <div
                className={`flex items-center gap-1 text-sm ${changeType === "positive" ? "text-green-600" : changeType === "negative" ? "text-red-600" : "text-gray-600"}`}
              >
                {changeType === "positive" ? (
                  <TrendingUp className="w-4 h-4" />
                ) : changeType === "negative" ? (
                  <TrendingDown className="w-4 h-4" />
                ) : (
                  <Minus className="w-4 h-4" />
                )}
                {changePercent && (
                  <span>
                    {changeType === "positive"
                      ? "+"
                      : changeType === "negative"
                        ? "-"
                        : "+"}
                    {changePercent.toFixed(1)}%
                  </span>
                )}
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          {coverage
            ? `Recorded market-guide history for ${coverage.coveredCopies} of ${coverage.totalCopies} copies in your current holdings. Owned quantities are included; graded copies and copies without matching history are excluded. This is not a record of past binder contents.`
            : "Recorded market-guide prices for this exact variant; not individual completed sales."}
        </p>
        <div className="flex items-center gap-2 mb-4 justify-end flex-wrap">
          {periods.map((period) => (
            <Button
              key={period.name}
              variant={currentPeriod === period.name ? "default" : "outline"}
              onClick={() => setCurrentPeriod(period.name)}
              className="text-sm py-0! px-4 h-auto"
              style={{
                paddingTop: "4px",
                paddingBottom: "4px",
              }}
            >
              {period.label ?? period.name}
            </Button>
          ))}
        </div>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient
                  id="positiveGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="negativeGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="neutralGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#bcbbcc" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#bcbbcc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="date"
                className="text-xs text-gray-600 dark:text-muted-foreground"
              />
              <YAxis
                className="text-xs text-gray-600 dark:text-muted-foreground"
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={CustomTooltip} isAnimationActive={false} />
              <Area
                type="linear"
                dataKey="value"
                stroke={
                  changeType === "negative"
                    ? "#ef4444"
                    : changeType === "positive"
                      ? "#10b981"
                      : "#bcbbcc"
                }
                strokeWidth={2}
                fill={
                  changeType === "negative"
                    ? "url(#negativeGradient)"
                    : changeType === "positive"
                      ? "url(#positiveGradient)"
                      : "url(#neutralGradient)"
                }
                fillOpacity={0.8}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-600 dark:text-muted-foreground">
            <div className="text-center">
              <p className="text-lg font-medium mb-2">No Value History Yet</p>
              <p className="text-sm">
                No matching historical observations are available for this period.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BinderValueChart;
